import { getAssetsUrlForCDN } from '@/utils/template';

export interface ProviderItem {
  slug: string;
}

const PROVIDERS_IMAGES_BASE = '/assets/images/games-filter-providers';

export const STATIC_PROVIDERS: ProviderItem[] = [
  { slug: 'pgsoft' },
  { slug: 'evolution' },
  { slug: 'spribe' },
  { slug: 'pragmaticplay' },
  { slug: 'nolimitcity' },
  { slug: 'amusnet' },
  { slug: 'fazi' },
  { slug: 'barbarabang' },
  { slug: 'platipus' },
  { slug: 'playson' },
  { slug: 'nsoft' },
  { slug: 'hype' },
  { slug: '1x2-gaming' },
  { slug: 'banana' },
  { slug: 'popok' },
  { slug: 'bgaming' },
  { slug: 'red-tiger' },
  { slug: 'evoplay' },
  { slug: 'ezugi' },
  { slug: 'imaginelive' },
  { slug: 'playtech' },
  { slug: 'smartsoft' },
  { slug: 'hacksaw' },
  { slug: 'rubyplay' },
  { slug: 'tada' },
  { slug: 'caletagaming' },
  { slug: 'booming' },
  { slug: 'cpgames' },
];

export const PROVIDER_SLUGS: string[] = STATIC_PROVIDERS.map((p) => p.slug);

export const PROVIDER_IMAGE_OVERRIDES: Record<
  string,
  Record<string, string>
> = {
  pgsoft: { transform: 'scale(1.3) translateY(0px)' },
  evolution: { transform: 'scale(1.5) translateY(0px)' },
  spribe: { transform: 'scale(1) translateY(0px)' },
  pragmaticplay: { transform: 'scale(2.2) translateY(0px)' },
  nolimitcity: { transform: 'scale(1.5) translateY(0px)' },
  amusnet: { transform: 'scale(1.75) translateY(0px)' },
  fazi: { transform: 'scale(1.35) translateY(0px)' },
  barbarabang: {
    filter: 'none',
    transform: 'scale(0.95) translateY(0px)',
  },
  platipus: { transform: 'scale(1.35) translateY(0px)' },
  playson: { transform: 'scale(1.9) translateY(0px)' },
  nsoft: { transform: 'scale(2.7) translateY(0px)' },
  hype: { transform: 'scale(1.8) translateY(0px)' },
  '1x2-gaming': { transform: 'scale(1.7) translateY(0px)' },
  banana: { transform: 'scale(1.2) translateY(0px)' },
  popok: { transform: 'scale(1.35) translateY(0px)' },
  bgaming: { transform: 'scale(1.7) translateY(0px)' },
  'red-tiger': { transform: 'scale(1.4) translateY(0px)' },
  evoplay: { transform: 'scale(2.1) translateY(0px)' },
  ezugi: { transform: 'scale(1.35) translateY(1.5px)' },
  imaginelive: { transform: 'scale(2) translateY(1.5px)' },
  playtech: { transform: 'scale(1.8) translateY(0px)' },
  smartsoft: { transform: 'scale(1.7) translateY(0px)' },
  hacksaw: { transform: 'scale(1.6) translateY(0px)' },
  rubyplay: { transform: 'scale(2.2) translateY(0px)' },
  tada: { transform: 'scale(1.6) translateY(0px)' },
  caletagaming: { transform: 'scale(1.6) translateY(0px)' },
  booming: { transform: 'scale(1.6) translateY(0px)' },
  cpgames: { transform: 'scale(1.3) translateY(0px)' },
};

export function getProviderImage(slug: string): string | undefined {
  if (PROVIDER_SLUGS.includes(slug)) {
    return getAssetsUrlForCDN(`${PROVIDERS_IMAGES_BASE}/${slug}.png`);
  }

  return undefined;
}

export function getProviderStyle(slug: string): Record<string, string> {
  const override = PROVIDER_IMAGE_OVERRIDES[slug] || {};

  return Object.assign(
    {
      display: 'block',
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain',
    },
    override,
  );
}

/**
 * Aliases de slugs abreviados para o slug canônico do provider.
 * Ex: "pragmatic" no slug da categoria mapeia para "pragmaticplay".
 */
const PROVIDER_ALIASES: Record<string, string> = {
  pragmatic: 'pragmaticplay',
};

/**
 * Extrai slug de provedora conhecida de um slug de categoria.
 * Primeiro tenta match direto com slugs canônicos (ordenados por tamanho
 * descendente para evitar match parcial). Depois tenta aliases.
 *
 * Exemplos:
 *   "slots-popok" → "popok"
 *   "slots-pragmatic" → "pragmaticplay" (via alias)
 *   "aovivo-pragmaticplay" → "pragmaticplay" (match direto)
 *   "todos-slots-padrinho" → undefined
 */
export function extractProviderSlug(categorySlug: string): string | undefined {
  const normalized = categorySlug.toLowerCase();

  // 1. Match direto com slugs canônicos (mais longo primeiro)
  const sortedSlugs = [...PROVIDER_SLUGS].sort((a, b) => b.length - a.length);

  for (const providerSlug of sortedSlugs) {
    if (normalized.includes(providerSlug)) {
      return providerSlug;
    }
  }

  // 2. Match via aliases (mais longo primeiro)
  const sortedAliases = Object.keys(PROVIDER_ALIASES).sort(
    (a, b) => b.length - a.length,
  );

  for (const alias of sortedAliases) {
    if (normalized.includes(alias)) {
      return PROVIDER_ALIASES[alias];
    }
  }

  return undefined;
}
